import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ExpandMore,
  Merge,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface DuplicateGroup {
  matchField: string;
  matchValue: string;
  candidates: any[];
}

interface DuplicateSummary {
  totalDuplicateGroups: number;
  byEmail: number;
  byName: number;
  byPhone: number;
  multiField: number;
}

const CandidateDeduplication: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [duplicates, setDuplicates] = useState<any>(null);
  const [summary, setSummary] = useState<DuplicateSummary | null>(null);
  const [selectedForMerge, setSelectedForMerge] = useState<Set<number>>(new Set());
  const [primaryCandidate, setPrimaryCandidate] = useState<number | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [mergeDialog, setMergeDialog] = useState(false);

  // Check permissions
  useEffect(() => {
    if (!user || !['admin', 'hr'].includes(user.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchDuplicates = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `/api/deduplication/duplicates?includeArchived=${includeArchived}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      const data = await response.json();
      setSummary(data.summary);
      setDuplicates(data.duplicates);
    } catch (error) {
      console.error('Failed to fetch duplicates:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDuplicates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeArchived]);

  const handleSelectCandidate = (candidateId: number) => {
    const newSelected = new Set(selectedForMerge);
    if (newSelected.has(candidateId)) {
      newSelected.delete(candidateId);
      if (primaryCandidate === candidateId) {
        setPrimaryCandidate(null);
      }
    } else {
      newSelected.add(candidateId);
    }
    setSelectedForMerge(newSelected);
  };

  const handleSetPrimary = (candidateId: number) => {
    setPrimaryCandidate(candidateId);
    const newSelected = new Set(selectedForMerge);
    newSelected.add(candidateId);
    setSelectedForMerge(newSelected);
  };

  const handleMerge = async () => {
    if (!primaryCandidate || selectedForMerge.size < 2) return;

    const duplicateIds = Array.from(selectedForMerge).filter(id => id !== primaryCandidate);

    try {
      const response = await fetch('/api/deduplication/merge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          primaryId: primaryCandidate,
          duplicateIds,
          deleteDuplicates: false,
        }),
      });

      if (response.ok) {
        setMergeDialog(false);
        setSelectedForMerge(new Set());
        setPrimaryCandidate(null);
        fetchDuplicates();
      }
    } catch (error) {
      console.error('Failed to merge candidates:', error);
    }
  };

  const renderDuplicateGroup = (group: DuplicateGroup, groupType: string) => {
    return (
      <Accordion key={`${groupType}-${group.matchValue}`}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2} width="100%">
            <Chip
              label={group.matchField}
              size="small"
              color={group.matchField === 'email' ? 'primary' : 
                     group.matchField === 'name' ? 'secondary' : 'default'}
            />
            <Typography variant="body1" sx={{ flexGrow: 1 }}>
              {group.matchValue || '(empty)'}
            </Typography>
            <Chip
              label={`${group.candidates.length} duplicates`}
              size="small"
              color="warning"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Select</TableCell>
                  <TableCell>Primary</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Job</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {group.candidates.map((candidate) => (
                  <TableRow 
                    key={candidate.id}
                    selected={selectedForMerge.has(candidate.id)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedForMerge.has(candidate.id)}
                        onChange={() => handleSelectCandidate(candidate.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color={primaryCandidate === candidate.id ? 'primary' : 'default'}
                        onClick={() => handleSetPrimary(candidate.id)}
                      >
                        <CheckCircle />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      {candidate.first_name} {candidate.last_name}
                    </TableCell>
                    <TableCell>{candidate.email || '-'}</TableCell>
                    <TableCell>{candidate.phone || '-'}</TableCell>
                    <TableCell>{candidate.job_title || '-'}</TableCell>
                    <TableCell>
                      {new Date(candidate.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={candidate.is_archived ? 'Archived' : 'Active'}
                        size="small"
                        color={candidate.is_archived ? 'default' : 'success'}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => navigate(`/candidates/${candidate.id}`)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Candidate Deduplication
      </Typography>

      {summary && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Duplicate Summary
          </Typography>
          <Box display="flex" gap={3}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Total Groups
              </Typography>
              <Typography variant="h4">
                {summary.totalDuplicateGroups}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                By Email
              </Typography>
              <Typography variant="h4" color="primary">
                {summary.byEmail}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                By Name
              </Typography>
              <Typography variant="h4" color="secondary">
                {summary.byName}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                By Phone
              </Typography>
              <Typography variant="h4">
                {summary.byPhone}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Multi-field
              </Typography>
              <Typography variant="h4" color="warning.main">
                {summary.multiField}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <FormControlLabel
          control={
            <Switch
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
          }
          label="Include archived candidates"
        />
        
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Merge />}
            disabled={selectedForMerge.size < 2 || !primaryCandidate}
            onClick={() => setMergeDialog(true)}
          >
            Merge Selected ({selectedForMerge.size})
          </Button>
        </Box>
      </Box>

      {selectedForMerge.size >= 2 && !primaryCandidate && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select a primary candidate by clicking the checkmark icon
        </Alert>
      )}

      {duplicates && (
        <Box>
          {duplicates.byEmail.length > 0 && (
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>
                Email Duplicates
              </Typography>
              {duplicates.byEmail.map((group: DuplicateGroup) => 
                renderDuplicateGroup(group, 'email')
              )}
            </Box>
          )}

          {duplicates.byName.length > 0 && (
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>
                Name Duplicates
              </Typography>
              {duplicates.byName.map((group: DuplicateGroup) => 
                renderDuplicateGroup(group, 'name')
              )}
            </Box>
          )}

          {duplicates.byPhone.length > 0 && (
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>
                Phone Duplicates
              </Typography>
              {duplicates.byPhone.map((group: DuplicateGroup) => 
                renderDuplicateGroup(group, 'phone')
              )}
            </Box>
          )}

          {duplicates.combined.length > 0 && (
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>
                Multi-field Duplicates
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                These candidates match on multiple fields and are likely the same person
              </Alert>
              {/* Render combined duplicates */}
            </Box>
          )}
        </Box>
      )}

      <Dialog open={mergeDialog} onClose={() => setMergeDialog(false)}>
        <DialogTitle>Confirm Merge</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action will merge {selectedForMerge.size - 1} candidates into the primary candidate.
            Duplicate candidates will be archived (not deleted).
          </Alert>
          <Typography>
            Primary candidate ID: {primaryCandidate}
          </Typography>
          <Typography>
            Duplicates to merge: {Array.from(selectedForMerge).filter(id => id !== primaryCandidate).join(', ')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeDialog(false)}>Cancel</Button>
          <Button onClick={handleMerge} variant="contained" color="warning">
            Merge Candidates
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CandidateDeduplication;